import {
    AggregateFunctions,
    AggregateQueryInterval,
    AggregateQueryMode,
    ApiQuery,
    ApiQueryEndpoints,
    Filter,
    FilterOperators,
} from "~/modules/filters/types";
import { MEASURE_SLUGS } from "~/consts";
import {
    LegendProps,
    NavigatorControlState,
    VisualizationConfig,
    VisualizationMode,
    VisualizationModeTypes,
} from "~/modules/visualization/components/Timeseries/types";
import {
    TODAYS_DATE,
    TWO_YEARS_AGO,
} from "~/modules/visualization/components/TimeSeriesChartV3/consts";
import { TOKEN_TIMESERIES_V4_API_TOOLS_CONTROLS } from "~/modules/visualization/components/Timeseries/consts";
import { TokenTimeseriesColumns, TokenTimeseriesGroupBy } from "../types";
import { ExportTableNames } from "~/modules/downloads/types";

// // // //

const BASE_QUERY: ApiQuery = {
    sort: {
        direction: "asc",
        field: "date",
    },
    pagination: {
        page: 1,
        perPage: 25,
    },
};

const visualizationConfig: VisualizationConfig = {
    area: true,
    enableZoom: false,
    showTotal: true,
    height: 350,
    legend: {
        itemWidth: 125,
        layout: "vertical",
        align: "right",
        verticalAlign: "top",
    },
};

// // // //

export function getTransferVolumeChart(props: {
    key: string;
    filters: Filter[];
    defaultGroupBy: TokenTimeseriesGroupBy;
    enableGroupByControls: boolean;
    groupByOptions: TokenTimeseriesGroupBy[];
    groupByForceUpdate?: boolean;
    loginRequired?: boolean;
    groupLimit?: number;
    legend?: LegendProps;
    navigator?: NavigatorControlState;
    height?: number;
}): VisualizationMode {
    const {
        key,
        filters,
        enableGroupByControls,
        defaultGroupBy,
        groupByOptions,
        groupByForceUpdate = true,
        loginRequired = false,
        groupLimit,
        legend = visualizationConfig.legend,
        navigator = {
            enabled: true,
            active: true,
            forceUpdate: true,
            xAxis: {
                min: Number(TWO_YEARS_AGO),
                max: Number(TODAYS_DATE),
            },
        },
        height,
    } = props;
    return {
        key,
        type: VisualizationModeTypes.column,
        loginRequired,
        content: {
            label: "Transfer Volume",
        },
        visualizationConfig: {
            ...visualizationConfig,
            showTotal: true,
            legend,
            ...(height !== undefined && { height }),
        },
        visualizationControls: {
            stacked: {
                enabled: false,
                active: false,
                forceUpdate: true,
            },
            legend: {
                enabled: true,
                active: true,
                forceUpdate: true,
            },
            navigator: {
                ...navigator,
            },
        },
        endpoint: ApiQueryEndpoints.TokenTimeseriesV4,
        query: {
            ...BASE_QUERY,
            aggregate: {
                aggregateFunction: AggregateFunctions.Sum,
                groupBy: defaultGroupBy,
                groupLimit,
                mode: AggregateQueryMode.flow,
            },
            filter: {
                operator: FilterOperators.And,
                filters: [
                    ...filters,
                    {
                        field: TokenTimeseriesColumns.measureSlug,
                        operator: FilterOperators.Equals,
                        value: MEASURE_SLUGS.daily_transfer_volume_dollar,
                    },
                ],
            },
        },
        queryControls: {
            groupBy: {
                enabled: enableGroupByControls,
                forceUpdate: groupByForceUpdate,
                value: defaultGroupBy,
                allowedOptions: groupByOptions,
            },
            interval: {
                enabled: true,
                forceUpdate: true,
                value: AggregateQueryInterval.week,
                allowedOptions: [
                    AggregateQueryInterval.day,
                    AggregateQueryInterval.week,
                    AggregateQueryInterval.month,
                    AggregateQueryInterval.quarter,
                    AggregateQueryInterval.year,
                ],
            },
        },
        apiTools: TOKEN_TIMESERIES_V4_API_TOOLS_CONTROLS,
        dataExportControls: {
            enabled: true,
            filename: key,
            exportTableName: ExportTableNames.token_timeseries_v4,
        },
    };
}
